import {
  reactElements,
  widget,
  info,
  providers,
  bindings,
  routers,
  pages,
} from '@wix/editor';
import { createClient } from '@wix/sdk';
import { CompRef } from '@wix/document-services-types';
import { IPlatformInfraPublicAPI } from './types';
import {
  ManifestServicesHost,
  ManifestBuilderStrategy,
  IVirtualManifestBuilderProps,
} from '@wix/editor-platform-manifest-services';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

declare global {
  // eslint-disable-next-line no-var
  var $sdk: any;
}

export class PlatformDevTools {
  constructor(
    private infra: IPlatformInfraPublicAPI,
    private manifestServicesHost: ManifestServicesHost,
    private editorPointerServiceAPI: EditorPointerServiceAPI,
  ) {}

  register() {
    if (typeof window === 'undefined' || globalThis.$sdk) {
      return;
    }

    globalThis.$sdk = this.#exposeSDKDevTools();
  }

  #exposeSDKDevTools() {
    /**
     * fix inner elements path spread from the dev tools
     */
    const fixEditorPointers = (editorPointers: EditorPointer<CompRef>[]) => {
      return editorPointers.flat().map((editorPointer) => {
        return this.editorPointerServiceAPI.buildEditorPointer(
          this.editorPointerServiceAPI.getDocumentPointer(editorPointer),
          this.editorPointerServiceAPI.getInnerElementPath(editorPointer),
        );
      });
    };

    return {
      getHostContext: (
        props: {
          contextType?: string;
        } & IVirtualManifestBuilderProps,
      ) => {
        return this.infra.getSDKHostContext(props);
      },
      buildManifestAPI: (props: IVirtualManifestBuilderProps) => {
        return this.manifestServicesHost.buildManifestAPI(props);
      },
      reactElements: (
        editorPointers: EditorPointer<CompRef>[],
        contextType?: string,
      ) => {
        const host = this.infra.getSDKHost({
          editorPointers: fixEditorPointers(editorPointers),
          contextType,
          strategy: ManifestBuilderStrategy.Intersection,
        });

        return createClient({ host, modules: { reactElements } }).reactElements;
      },
      info: () => {
        const host = this.infra.getSDKHost({});
        return createClient({ host, modules: { info } }).info;
      },
      providers: (
        editorPointers: EditorPointer<CompRef>[],
        contextType?: string,
      ) => {
        const host = this.infra.getSDKHost({
          editorPointers: fixEditorPointers(editorPointers),
          contextType,
          strategy: ManifestBuilderStrategy.Merge,
        });

        return createClient({ host, modules: { providers } }).providers;
      },
      bindings: (
        editorPointers: EditorPointer<CompRef>[],
        contextType?: string,
      ) => {
        const host = this.infra.getSDKHost({
          editorPointers: fixEditorPointers(editorPointers),
          contextType,
          strategy: ManifestBuilderStrategy.Merge,
        });

        return createClient({ host, modules: { bindings } }).bindings;
      },
      widget: () => {
        const host = this.infra.getSDKHost({});

        return createClient({ host, modules: { widget } }).widget;
      },
      routers: (appDefinitionId?: string) => {
        const host = this.infra.getSDKHost({ appDefinitionId });
        return createClient({ host, modules: { routers } }).routers;
      },
      pages: (appDefinitionId?: string) => {
        const host = this.infra.getSDKHost({ appDefinitionId });
        return createClient({ host, modules: { pages } }).pages;
      },
      getComponentsService: () => {
        return this.manifestServicesHost.getComponentsService();
      },
      getPublicComponentsService: () => {
        return this.manifestServicesHost.getComponentsService();
      },
      getManifest: (editorPointer: EditorPointer<CompRef>) => {
        return this.manifestServicesHost.getManifestByEditorPointer(
          editorPointer,
        );
      },
      getBuilderManifests: () => {
        return this.manifestServicesHost.getBuilderManifests();
      },
      getBuilderPanels: () => {
        return this.manifestServicesHost.getBuilderPanels();
      },
      clearCaches: () => {
        this.manifestServicesHost.manifestChangeDetection.notifyGlobalPatch();
      },
      stats: () => {
        return this.manifestServicesHost.manifestServiceStats;
      },
      virtualElement: (editorPointers: EditorPointer<CompRef>[]) => {
        return this.manifestServicesHost.virtualElementProvider.buildVirtualElement(
          {
            editorPointers: fixEditorPointers(editorPointers),
          },
        );
      },
      traverse: () => {
        throw new Error('use .virtualElement() instead');
      },
    };
  }
}
